import { useTheme } from '@mui/material'
import { PositionDto, YardBlockDto, YardBlockType } from '@yard/app/api'
import React, { useCallback, useMemo } from 'react'

const FLEX_BLOCK_BAYS = 3
const FLEX_BLOCK_ROWS = 3
const CELL_GAP = 4
const SVG_PAD = 12
// Intrinsic viewport. The component itself renders at width='100%' with this
// viewBox, so dense blocks like 16x6 still get ~37x62 px per cell once the
// dialog gives the SVG the room it needs. Per-tier rendering was abandoned
// because tierH collapsed to ~3px on these blocks and made clicks unhittable.
const VIEW_W = 640
const VIEW_H = 400
const AXIS_LABEL_WIDTH = 28
const AXIS_LABEL_HEIGHT = 22

export interface BlockMiniMapProps {
  block: YardBlockDto
  occupiedPositions: PositionDto[]
  selectedPositions: PositionDto[]
  onSelectionChange: (positions: PositionDto[]) => void
}

const blockDimensions = (block: YardBlockDto): { bays: number; rows: number; tiers: number } => {
  const tiers = Math.max(block.maxTiers ?? 1, 1)
  return block.type === YardBlockType.FlexibleBlock
    ? { bays: FLEX_BLOCK_BAYS, rows: FLEX_BLOCK_ROWS, tiers }
    : { bays: block.maxBays, rows: block.maxRows, tiers }
}

const cellKey = (bay: number, row: number) => `${bay}-${row}`

const tallyByCell = (positions: PositionDto[]): Map<string, number[]> => {
  const m = new Map<string, number[]>()
  for (const p of positions) {
    const k = cellKey(p.bay, p.row)
    const list = m.get(k) ?? []
    list.push(p.tier)
    m.set(k, list)
  }
  return m
}

export const BlockMiniMap: React.FC<BlockMiniMapProps> = ({
  block,
  occupiedPositions,
  selectedPositions,
  onSelectionChange,
}) => {
  const theme = useTheme()
  const { bays, rows, tiers } = blockDimensions(block)

  const occupiedByCell = useMemo(() => tallyByCell(occupiedPositions), [occupiedPositions])
  const selectedByCell = useMemo(() => tallyByCell(selectedPositions), [selectedPositions])

  const handleCellClick = useCallback(
    (bay: number, row: number) => {
      const k = cellKey(bay, row)
      const occupied = occupiedByCell.get(k) ?? []
      const selected = selectedByCell.get(k) ?? []
      const freeCapacity = tiers - occupied.length
      if (freeCapacity <= 0) return
      // No-op when the cell is already at max selection. Decrement happens
      // via the per-cell "−" badge (see handleRemoveOneTier).
      if (selected.length >= freeCapacity) return
      // Drop on top of the existing stack — operators don't pick individual
      // tiers; containers land on the next free level above any occupied
      // (or already-queued) tier.
      const usedTiers = [...occupied, ...selected]
      const maxUsed = usedTiers.length > 0 ? Math.max(...usedTiers) : 0
      onSelectionChange([...selectedPositions, { bay, row, tier: maxUsed + 1 }])
    },
    [occupiedByCell, selectedByCell, selectedPositions, tiers, onSelectionChange],
  )

  const handleRemoveOneTier = useCallback(
    (bay: number, row: number) => {
      const selected = selectedByCell.get(cellKey(bay, row)) ?? []
      if (selected.length === 0) return
      const maxSelectedTier = Math.max(...selected)
      onSelectionChange(
        selectedPositions.filter(
          p => !(p.bay === bay && p.row === row && p.tier === maxSelectedTier),
        ),
      )
    },
    [selectedByCell, selectedPositions, onSelectionChange],
  )

  const gridX0 = SVG_PAD + AXIS_LABEL_WIDTH
  const gridY0 = SVG_PAD + AXIS_LABEL_HEIGHT
  const gridW = Math.max(VIEW_W - SVG_PAD * 2 - AXIS_LABEL_WIDTH, 1)
  const gridH = Math.max(VIEW_H - SVG_PAD * 2 - AXIS_LABEL_HEIGHT, 1)
  const cellW = Math.max((gridW - (bays - 1) * CELL_GAP) / bays, 1)
  const cellH = Math.max((gridH - (rows - 1) * CELL_GAP) / rows, 1)

  const elts: React.ReactElement[] = []

  // Axis labels (bay numbers along the top, row numbers along the left).
  for (let bay = 1; bay <= bays; bay++) {
    const x = gridX0 + (bay - 1) * (cellW + CELL_GAP) + cellW / 2
    elts.push(
      <text
        key={`axis-bay-${bay}`}
        x={x}
        y={gridY0 - 6}
        textAnchor='middle'
        fontSize={11}
        fontWeight={600}
        fill={theme.palette.text.secondary}
        fontFamily='ui-monospace, monospace'
        pointerEvents='none'
      >
        {String(bay).padStart(2, '0')}
      </text>,
    )
  }
  for (let row = 1; row <= rows; row++) {
    const y = gridY0 + (row - 1) * (cellH + CELL_GAP) + cellH / 2
    elts.push(
      <text
        key={`axis-row-${row}`}
        x={gridX0 - 6}
        y={y}
        textAnchor='end'
        dominantBaseline='central'
        fontSize={11}
        fontWeight={600}
        fill={theme.palette.text.secondary}
        fontFamily='ui-monospace, monospace'
        pointerEvents='none'
      >
        {String(row).padStart(2, '0')}
      </text>,
    )
  }

  for (let bay = 1; bay <= bays; bay++) {
    for (let row = 1; row <= rows; row++) {
      const k = cellKey(bay, row)
      const occupied = occupiedByCell.get(k) ?? []
      const selectedCount = selectedByCell.get(k)?.length ?? 0
      const freeCapacity = tiers - occupied.length
      const isFull = freeCapacity === 0
      const x = gridX0 + (bay - 1) * (cellW + CELL_GAP)
      // Row 1 sits at the top of the canvas to match the orientation users
      // see in the yard-view block detail (rows numbered top-down).
      const y = gridY0 + (row - 1) * (cellH + CELL_GAP)

      const fill = isFull
        ? theme.palette.grey[700]
        : selectedCount === 0
          ? theme.palette.grey[100]
          : selectedCount === freeCapacity
            ? theme.palette.primary.main
            : theme.palette.primary.light
      const stroke = isFull ? theme.palette.grey[800] : theme.palette.grey[400]
      const labelColor =
        isFull || selectedCount > 0 ? theme.palette.common.white : theme.palette.text.secondary
      const label = isFull ? 'Full' : `${selectedCount}/${freeCapacity}`
      const ariaLabel = `Bay ${bay}, row ${row}: ${selectedCount} of ${freeCapacity} free tier(s) selected${
        isFull ? ' (full)' : ''
      }`

      elts.push(
        <g
          key={k}
          role={isFull ? undefined : 'button'}
          aria-label={ariaLabel}
          tabIndex={isFull ? undefined : 0}
          onClick={() => handleCellClick(bay, row)}
          onKeyDown={event => {
            if (isFull) return
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              handleCellClick(bay, row)
            }
          }}
          style={{ cursor: isFull ? 'not-allowed' : 'pointer' }}
        >
          <title>{ariaLabel}</title>
          <rect
            x={x}
            y={y}
            width={cellW}
            height={cellH}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
            rx={2}
          />
          <text
            x={x + cellW / 2}
            y={y + cellH / 2}
            textAnchor='middle'
            dominantBaseline='central'
            fontSize={Math.max(Math.min(cellH * 0.3, 16), 10)}
            fontWeight={600}
            fill={labelColor}
            pointerEvents='none'
          >
            {label}
          </text>
        </g>,
      )

      // Per-cell decrement affordance: a small "−" badge tucked into the
      // bottom-right corner of any selected cell. Lets the user back out one
      // tier without having to clear the whole cell. Rendered as a sibling
      // group (not nested inside the cell <g>) so stopPropagation reliably
      // prevents the cell's own click handler from firing.
      if (selectedCount > 0) {
        const badgeR = Math.max(Math.min(cellH * 0.13, 6.5), 4.5)
        const badgeCx = x + cellW - badgeR - 2
        const badgeCy = y + cellH - badgeR - 2
        const badgeAria = `Remove last tier from bay ${bay}, row ${row}`
        elts.push(
          <g
            key={`${k}-minus`}
            role='button'
            aria-label={badgeAria}
            tabIndex={0}
            onClick={event => {
              event.stopPropagation()
              handleRemoveOneTier(bay, row)
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                handleRemoveOneTier(bay, row)
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <title>{badgeAria}</title>
            <circle
              cx={badgeCx}
              cy={badgeCy}
              r={badgeR}
              fill={theme.palette.common.white}
              stroke={theme.palette.primary.main}
              strokeWidth={1}
            />
            <text
              x={badgeCx}
              y={badgeCy + 0.5}
              textAnchor='middle'
              dominantBaseline='central'
              fontSize={badgeR * 1.7}
              fontWeight={700}
              fill={theme.palette.primary.main}
              pointerEvents='none'
              fontFamily='ui-monospace, monospace'
            >
              −
            </text>
          </g>,
        )
      }
    }
  }

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width='100%'
      role='img'
      aria-label={`Block ${block.name} mini-map`}
      style={{ maxHeight: VIEW_H, userSelect: 'none' }}
    >
      {elts}
    </svg>
  )
}
